import React, { useState, useMemo, useRef, useEffect } from "react";
import { htlApi } from "../../config/api";
import { apiClient } from "../../utils/apiClient";
import "./HappyThinkingLaboratory.scss";

const CATEGORIES = ["Meditation", "Psychology", "Counseling", "Research", "Other"];
const STATUSES = ["Working", "Under Maintenance", "Broken", "Replaced"];

const BLANK_FORM = {
  _id: null, Equipment_Name: "", Category: "Meditation", Description: "",
  Quantity: "1", Status: "Working", Date_Added: new Date().toISOString().slice(0, 10),
};

const HappyThinkingLaboratory = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalType, setModalType] = useState("");
  const [formData, setFormData] = useState(BLANK_FORM);
  const [confirmId, setConfirmId] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [dropdownOpenId, setDropdownOpenId] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const [liveStatus, setLiveStatus] = useState("Available");
  const [announcement, setAnnouncement] = useState("The laboratory is open for student counseling sessions.");
  const [isEditingAnn, setIsEditingAnn] = useState(false);
  const [annInput, setAnnInput] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const response = await apiClient(htlApi);
      const result = await response.json();
      if (response.ok) { setItems(result.data || []); }
    } catch (error) {
      console.error("Error fetching HTL items:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const onClose = () => setDropdownOpenId(null);
    window.addEventListener("scroll", onClose, true);
    return () => window.removeEventListener("scroll", onClose, true);
  }, []);

  const handleDotsClick = (e, id) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + 8, left: rect.right - 180 });
    setDropdownOpenId(id);
  };

  const openAddModal = () => { setFormData(BLANK_FORM); setModalType("add"); setDropdownOpenId(null); };
  const openEditModal = (item) => { setFormData({ ...item }); setModalType("edit"); setDropdownOpenId(null); };
  const closeModal = () => { setModalType(""); setFormData(BLANK_FORM); };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const method = modalType === "add" ? "POST" : "PUT";
      const url = modalType === "add" ? htlApi : `${htlApi}/${formData._id}`;
      const response = await apiClient(url, { method, body: JSON.stringify(formData) });
      if (response.ok) { fetchItems(); closeModal(); }
    } catch (error) {
      console.error("Error saving HTL item:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      await apiClient(`${htlApi}/${id}`, { method: "DELETE" });
      fetchItems();
      setConfirmId(null); setDropdownOpenId(null);
    } catch (error) {
      console.error("Error deleting item:", error);
    } finally {
      setLoading(false);
    }
  };

  const filtered = items.filter(i => 
    (i.Equipment_Name || "").toLowerCase().includes(searchText.toLowerCase()) || 
    (i.Category || "").toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <div className="htl-page">
      <div className="htl-header">
        <div className="htl-header-left">
          <div className="htl-header-icon">🧠</div>
          <div><h1>Happy Thinking Laboratory</h1><p>Manage wellness laboratory resources, psychology tools, and real-time laboratory status.</p></div>
        </div>
        <div className="htl-header-actions">
          <button className="htl-btn-primary" onClick={openAddModal}>Add Resource</button>
        </div>
      </div>

      <div className="htl-realtime-bar">
        <div className="htl-live-clock"><div className="htl-live-dot"></div><div><span className="htl-time">{new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span><span className="htl-date">{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}</span></div></div>
        <div className="htl-status-control">
          <span className="htl-status-label">Current Lab Status</span>
          <div className="htl-status-pills">
            {["Available", "In Session", "Reserved", "Closed"].map(s => (
              <button key={s} className={`htl-status-pill ${liveStatus === s ? "active" : ""}`} onClick={() => setLiveStatus(s)} style={liveStatus === s ? { borderColor: s === "Available" ? "#22c55e" : s === "In Session" ? "#6366f1" : s === "Reserved" ? "#f59e0b" : "#ef4444", color: "#fff", background: s === "Available" ? "#22c55e" : s === "In Session" ? "#6366f1" : s === "Reserved" ? "#f59e0b" : "#ef4444" } : {}}>
                <div className="htl-s-dot" style={{ background: s === "Available" ? "#22c55e" : s === "In Session" ? "#6366f1" : s === "Reserved" ? "#f59e0b" : "#ef4444" }}></div>{s}
              </button>
            ))}
          </div>
        </div>
        <div className="htl-ann-control">
          <span className="htl-status-label">Laboratory Announcement</span>
          {isEditingAnn ? (
            <div className="htl-ann-edit-row"><input value={annInput} onChange={e => setAnnInput(e.target.value)} /><button className="htl-ann-save" onClick={() => { setAnnouncement(annInput); setIsEditingAnn(false); }}>Save</button><button className="htl-ann-cancel" onClick={() => setIsEditingAnn(false)}>×</button></div>
          ) : (
            <div className="htl-ann-display" onClick={() => { setAnnInput(announcement); setIsEditingAnn(true); }}><span className="htl-ann-icon">📢</span><p className="htl-ann-text">{announcement}</p><span className="htl-ann-edit-icon">✎</span></div>
          )}
        </div>
      </div>

      <div className="htl-tabs">
        <button className={`htl-tab ${activeTab === "overview" ? "active" : ""}`} onClick={() => setActiveTab("overview")}>Overview</button>
        <button className={`htl-tab ${activeTab === "resources" ? "active" : ""}`} onClick={() => setActiveTab("resources")}>Resource Inventory</button>
      </div>

      {loading && <div className="loading-overlay">Loading...</div>}

      {activeTab === "overview" && (
        <div className="htl-overview-tab">
          <div className="htl-preview-banner">
            <div className="htl-pb-left">
              <span className="htl-pb-badge">Mental Wellness</span>
              <h2>About the Laboratory</h2>
              <p className="htl-pb-subtitle">Nurturing Minds for a Brighter Future</p>
              <p className="htl-pb-desc">A specialized space for psychological research, behavioral studies, and student counseling. Equipped with modern assessment tools and relaxation equipment.</p>
              <div className="htl-pb-meta-row"><span>📍 Psychology Dept, 2nd Floor</span><span>👥 Capacity: 15 Persons</span><span>⏰ 10:00 AM - 4:00 PM</span></div>
            </div>
            <div className="htl-pb-right">
              <div className="htl-pb-stats-grid">
                <div><strong>{items.length}</strong><span>Resources</span></div>
                <div><strong>{items.reduce((a, b) => a + (Number(b.Quantity) || 0), 0)}</strong><span>Units</span></div>
              </div>
            </div>
          </div>
          <div className="htl-section-head"><div><h2>Key Highlights</h2><p>Specialized zones within the laboratory.</p></div></div>
          <div className="htl-hl-grid">
            <div className="htl-hl-card"><div className="htl-hl-head"><div className="htl-hl-icon">🧘</div><h3>Relaxation Zone</h3></div><p>Equipped with biofeedback tools and meditation mats for stress management.</p></div>
            <div className="htl-hl-card"><div className="htl-hl-head"><div className="htl-hl-icon">📝</div><h3>Assessment Area</h3></div><p>Private cabins for psychometric testing and personality evaluations.</p></div>
            <div className="htl-hl-card"><div className="htl-hl-head"><div className="htl-hl-icon">📚</div><h3>Research Wing</h3></div><p>A library of psychology journals and research software for behavioral studies.</p></div>
          </div>
        </div>
      )}

      {activeTab === "resources" && (
        <div className="htl-equipment-tab">
          <div className="htl-toolbar">
            <div className="htl-search">🔍 <input type="text" placeholder="Search resources..." value={searchText} onChange={e => setSearchText(e.target.value)} /></div>
          </div>
          <div className="htl-eq-grid">
            {filtered.map(item => (
              <div key={item._id} className="htl-eq-card">
                <div className="htl-eq-head">
                  <div className="htl-eq-icon">🔬</div>
                  <div className="htl-eq-meta"><h3>{item.Equipment_Name}</h3><span className="htl-eq-cat">{item.Category}</span></div>
                  <button className="htl-dots" onClick={e => handleDotsClick(e, item._id)}>⋮</button>
                </div>
                <p className="htl-eq-desc">{item.Description}</p>
                <div className="htl-eq-info-row"><span>📦 Qty: {item.Quantity}</span><span>📅 Added: {item.Date_Added}</span></div>
                <div className="htl-eq-footer"><span className={`htl-eq-badge ${item.Status.toLowerCase().replace(' ', '-')}`}>{item.Status}</span></div>
              </div>
            ))}
          </div>
        </div>
      )}

      {dropdownOpenId && (
        <div ref={dropdownRef} className="htl-dropdown" style={{ position: "fixed", top: dropdownPos.top, left: dropdownPos.left, zIndex: 9999 }}>
          <button onClick={() => openEditModal(items.find(i => i._id === dropdownOpenId))}>Edit Resource</button>
          <button className="htl-dd-del" onClick={() => { setConfirmId(dropdownOpenId); setDropdownOpenId(null); }}>Delete Record</button>
        </div>
      )}

      {(modalType === "add" || modalType === "edit") && (
        <div className="htl-overlay" onClick={closeModal}>
          <div className="htl-form-modal" onClick={e => e.stopPropagation()}>
            <div className="htl-modal-head"><div><h2>{modalType === "add" ? "Add" : "Edit"} Resource</h2><p>Provide resource details, category and quantity.</p></div><button className="htl-modal-close" onClick={closeModal}>×</button></div>
            <form className="htl-form" onSubmit={handleSave}>
              <div className="htl-field full"><label>Resource Name *</label><input value={formData.Equipment_Name} onChange={e => setFormData({...formData, Equipment_Name: e.target.value})} required /></div>
              <div className="htl-field-row">
                <div className="htl-field"><label>Category</label><select value={formData.Category} onChange={e => setFormData({...formData, Category: e.target.value})}>{CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
                <div className="htl-field"><label>Status</label><select value={formData.Status} onChange={e => setFormData({...formData, Status: e.target.value})}>{STATUSES.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
              </div>
              <div className="htl-field-row">
                <div className="htl-field"><label>Quantity</label><input type="number" value={formData.Quantity} onChange={e => setFormData({...formData, Quantity: e.target.value})} /></div>
                <div className="htl-field"><label>Date Added</label><input type="date" value={formData.Date_Added} onChange={e => setFormData({...formData, Date_Added: e.target.value})} /></div>
              </div>
              <div className="htl-field full"><label>Description *</label><textarea value={formData.Description} onChange={e => setFormData({...formData, Description: e.target.value})} required rows="3" /></div>
              <div className="htl-form-footer"><button type="button" className="htl-btn-light" onClick={closeModal}>Cancel</button><button type="submit" className="htl-btn-primary">Save Resource</button></div>
            </form>
          </div>
        </div>
      )}

      {confirmId && (
        <div className="htl-overlay" onClick={() => setConfirmId(null)}>
          <div className="htl-confirm" onClick={e => e.stopPropagation()}>
            <h3>Delete Resource?</h3>
            <p>This action will permanently remove this resource from HTL inventory.</p>
            <div className="htl-confirm-actions"><button className="htl-btn-light" onClick={() => setConfirmId(null)}>Cancel</button><button className="htl-btn-danger" onClick={() => handleDelete(confirmId)}>Yes, Delete</button></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HappyThinkingLaboratory;